import React, { useEffect, useMemo, useState, useCallback, useRef } from "react";
import DataTable from "react-data-table-component";
import {
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Badge,
  Input,
  Label,
  FormGroup,
  Button,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import UserInfoModal from "../../components/Common/UserInfoModal";
import UserBroadcastsModal from "../userModeration/helper/userBroadcastsModal";
import UserReportsModal from "./helper/userReportsModal";
import { getReportedUsersList } from "./helper/reportedUsersApi";

const ReportedUsersPage = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [userInfoModal, setUserInfoModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [broadcastsModal, setBroadcastsModal] = useState(false);
  const [selectedUserForBroadcasts, setSelectedUserForBroadcasts] = useState(null);
  const [reportsModal, setReportsModal] = useState(false);
  const [selectedUserForReports, setSelectedUserForReports] = useState(null);

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [limit] = useState(50);
  const [totalRows, setTotalRows] = useState(0);

  // Refs for optimization
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);
  const isLoadingRef = useRef(false);
  const hasInitialFetchedRef = useRef(false);

  const handleUserIdClick = useCallback((userReport) => {
    setSelectedUser({
      userId: userReport.user_id,
      reportCount: userReport.report_count,
      firstReportedAt: userReport.first_reported_at,
      lastReportedAt: userReport.last_reported_at,
    });
    setUserInfoModal(true);
  }, []);

  const handleViewBroadcasts = useCallback((userReport) => {
    setSelectedUserForBroadcasts({
      userId: userReport.user_id,
      username: userReport.username,
    });
    setBroadcastsModal(true);
  }, []);

  const handleViewReports = useCallback((userReport) => {
    setSelectedUserForReports({
      userId: userReport.user_id,
      username: userReport.username,
    });
    setReportsModal(true);
  }, []);

  const columns = useMemo(
    () => [
      {
        name: <span className="font-weight-bold fs-13">User ID</span>,
        selector: (row) => row.user_id,
        sortable: true,
        cell: (row) => (
          <div>
            <div
              className="text-primary cursor-pointer"
              onClick={() => handleUserIdClick(row)}
              style={{ cursor: "pointer" }}
            >
              {row.user_id}
            </div>
          </div>
        ),
      },
      {
        name: <span className="font-weight-bold fs-13">Username</span>,
        selector: (row) => row.username,
        sortable: true,
      },
      {
        name: <span className="font-weight-bold fs-13">Report Count</span>,
        selector: (row) => row.report_count,
        sortable: true,
        cell: (row) => (
          <Badge color="danger" className="px-3 py-1">
            {row.report_count || 0}
          </Badge>
        ),
      },
      {
        name: <span className="font-weight-bold fs-13">View Reports</span>,
        cell: (row) => (
          <Button
            color="danger"
            size="sm"
            onClick={() => handleViewReports(row)}
            className="px-3"
          >
            <i className="mdi mdi-flag me-1"></i>View Reports
          </Button>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
      {
        name: <span className="font-weight-bold fs-13">First Reported At</span>,
        selector: (row) => row.first_reported_at,
        sortable: true,
        cell: (row) => {
          if (!row.first_reported_at) return "-";
          const date = new Date(row.first_reported_at);
          return date.toLocaleString();
        },
      },
      {
        name: <span className="font-weight-bold fs-13">Last Reported At</span>,
        selector: (row) => row.last_reported_at,
        sortable: true,
        cell: (row) => {
          if (!row.last_reported_at) return "-";
          const date = new Date(row.last_reported_at);
          return date.toLocaleString();
        },
      },
      {
        name: <span className="font-weight-bold fs-13">Actions</span>,
        cell: (row) => (
          <Button
            color="info"
            size="sm"
            onClick={() => handleViewBroadcasts(row)}
            className="px-3"
          >
            <i className="mdi mdi-broadcast me-1"></i>View Broadcasts
          </Button>
        ),
        ignoreRowClick: true,
        allowOverflow: true,
        button: true,
      },
    ],
    [handleUserIdClick, handleViewReports, handleViewBroadcasts]
  );

  const fetchList = useCallback(async () => {
    if (isLoadingRef.current) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    const abortController = new AbortController();
    abortControllerRef.current = abortController;

    isLoadingRef.current = true;
    setLoading(true);

    try {
      const filters = {
        limit,
        page: currentPage,
      };

      const result = await getReportedUsersList(filters);

      if (abortController.signal.aborted || !isMountedRef.current) {
        return;
      }

      setData(result.data);
      setTotalRows(result.pagination.totalRecords);
    } catch (error) {
      if (error.name !== "AbortError" && isMountedRef.current) {
        console.error("Error fetching reported users list:", error);
      }
    } finally {
      if (isMountedRef.current && !abortController.signal.aborted) {
        setLoading(false);
        isLoadingRef.current = false;
      }
      if (abortControllerRef.current === abortController) {
        abortControllerRef.current = null;
      }
    }
  }, [currentPage, limit]);

  // Initial fetch
  useEffect(() => {
    isMountedRef.current = true;
    if (!hasInitialFetchedRef.current) {
      fetchList();
      hasInitialFetchedRef.current = true;
    }

    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Fetch when page changes
  useEffect(() => {
    if (hasInitialFetchedRef.current) {
      fetchList();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage]);

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Reported Users" breadcrumbItem="Reported Users" />
          <Row>
            <Col xs={12}>
              <Card>
                <CardHeader>
                  <h4 className="card-title mb-0">
                    <i className="mdi mdi-account-alert me-2"></i>Reported Users
                  </h4>
                </CardHeader>
                <CardBody>
                  {/* Data Table */}
                  <DataTable
                    columns={columns}
                    data={data}
                    progressPending={loading}
                    pagination
                    paginationServer
                    paginationTotalRows={totalRows}
                    paginationDefaultPage={currentPage}
                    onChangePage={(page) => setCurrentPage(page)}
                    paginationPerPage={limit}
                    paginationRowsPerPageOptions={[10, 25, 50, 100]}
                    noDataComponent={
                      <div className="text-center py-4">
                        <i className="mdi mdi-account-off" style={{ fontSize: "3rem", color: "#6c757d" }}></i>
                        <p className="mt-3 text-muted">No reported users found</p>
                      </div>
                    }
                    highlightOnHover
                    pointerOnHover
                  />
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      <UserInfoModal
        isOpen={userInfoModal}
        toggle={() => {
          setUserInfoModal(false);
          setSelectedUser(null);
        }}
        userId={selectedUser?.userId}
        reportCount={selectedUser?.reportCount}
        firstReportedAt={selectedUser?.firstReportedAt}
        lastReportedAt={selectedUser?.lastReportedAt}
        onUpdate={() => {
          // Refresh the list after user action
          fetchList();
        }}
      />

      <UserBroadcastsModal
        isOpen={broadcastsModal}
        toggle={() => {
          setBroadcastsModal(false);
          setSelectedUserForBroadcasts(null);
        }}
        userId={selectedUserForBroadcasts?.userId}
        username={selectedUserForBroadcasts?.username}
      />

      <UserReportsModal
        isOpen={reportsModal}
        toggle={() => {
          setReportsModal(false);
          setSelectedUserForReports(null);
        }}
        userId={selectedUserForReports?.userId}
        username={selectedUserForReports?.username}
      />
    </React.Fragment>
  );
};

export default ReportedUsersPage;

